import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useAppConfigurationStore } from '@/stores/useAppConfigurationStore'
import { useHostNameStore } from '@/stores/useHostNameStore'
import { BRAND_V2_HOSTS } from '@/theme/hosts'

/**
 * useV2()
 * Returns a computed boolean indicating whether the current site should use the V2 variant.
 * Detection order:
 *  1) If any BRAND_V2_HOSTS classname is present on document.body -> true (immediate visual state).
 *  2) If app configuration has an id -> convert to dashed classname and check BRAND_V2_HOSTS.
 *  3) Fallback to hostNameStore.hostname and then window.location.hostname.
 *
 * Additionally, we listen to "app-config-refreshed" and "theme-changed" window events
 * to force a re-evaluation when ThemeSwitcher toggles themes or configuration is refreshed.
 */
export function useV2() {
  const appConfig = useAppConfigurationStore()
  const hostStore = useHostNameStore()

  // Internal tick to force recomputation on external events
  const tick = ref(0)
  const onPing = () => { tick.value++ }

  onMounted(() => {
    window.addEventListener('app-config-refreshed', onPing)
    window.addEventListener('theme-changed', onPing)
  })
  onBeforeUnmount(() => {
    window.removeEventListener('app-config-refreshed', onPing)
    window.removeEventListener('theme-changed', onPing)
  })

  return computed(() => {
    // tie computed to external event pings
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    tick.value

    try {
      // 1) If page already contains a V2 classname on body, prefer that (fast visual cue)
      if (BRAND_V2_HOSTS.some(t => document.body.classList.contains(t))) {
        return true
      }

      // 2) Use app configuration id if available (reactive)
      const cfgId = appConfig.configuration?.id
      const effectiveHost = (typeof cfgId === 'string' && cfgId.length > 0)
        ? cfgId
        : (hostStore.hostname || window.location.hostname || '')

      const cls = effectiveHost === 'localhost' ? 'localhost' : effectiveHost.replace(/\./g, '-')
      return BRAND_V2_HOSTS.includes(cls)
    } catch (err) {
      // Fail safe: default to V1
      // eslint-disable-next-line no-console
      console.warn('useV2: detection error', err)
      return false
    }
  })
}
